import { FC } from 'react';
import {
  AlertModalType,
  AlertModalTypeEnum,
  SuccessIcon,
  ToastStatus,
  useAlertModal,
  useStyledTheme,
  useToastNotifications,
  WarningIcon,
} from '@equifinance/shared';
import styled from 'styled-components';
import { TransitionError } from './Account';

const ResultMessages = styled.div`${({ theme }) => `
  line-height: ${theme.fontSize[6]};
`}`;

const ResultMessagesLabel = styled.p<{ isBold?: boolean }>`${({ theme, isBold }) => `
  font-weight: ${isBold ? theme.fontWeight.bold : theme.fontWeight.normal};
`}`;

const ResultMessagesList = styled.ul`${({ theme }) => `
  padding-left: ${theme.padding[6]};
  margin: 0;

  & > li::marker {
    font-size: ${theme.fontSize[1]};
  }
`}`;

function TransitionResultMessages({
  label,
  transitionErrors,
  isLabelBold = false,
}: {
  label: string,
  transitionErrors: TransitionError[],
  isLabelBold?: boolean
}) {
  const messages: string[] = transitionErrors.flatMap(({ messages }) => messages);
  return (
    <ResultMessages>
      <ResultMessagesLabel isBold={isLabelBold}>
        {label}
      </ResultMessagesLabel>
      <ResultMessagesList>
        {messages.map((message) => (
          <li key={message}>
            {message}
          </li>
        ))}
      </ResultMessagesList>
    </ResultMessages>
  );
}

export default function useTransitionResult() {
  const { setAlertModalProps, showAlertModal, closeAlertModal } = useAlertModal();
  const { addToastNotification } = useToastNotifications();
  const theme = useStyledTheme();

  const showModal = ({
    label,
    transitionErrors,
    IconComponent,
    isLabelBold = false,
    onClose,
    ...modalOptions
  }: {
    label: string,
    transitionErrors: TransitionError[],
    IconComponent: FC<{ stroke?: string; fill?: string; color?: string }>,
    isLabelBold?: boolean,
  } & Partial<AlertModalType>) => {
    setAlertModalProps({
      isOpen: true,
      cancelLabel: '',
      confirmLabel: 'OK',
      onClose: () => {
        onClose?.();
        closeAlertModal();
      },
      titleIcon: (
        <IconComponent
          color={theme.colours.primary[1]}
          stroke={theme.colours.primary[1]}
          fill={theme.colours.neutral[10]}
        />
      ),
      content: (
        <TransitionResultMessages
          label={label}
          transitionErrors={transitionErrors}
          isLabelBold={isLabelBold}
        />
      ),
      ...modalOptions,
    });
    showAlertModal();
  };

  const showSuccessModal = (
    title: AlertModalType['title'],
    label: string,
    transitionErrors: TransitionError[],
    onClose?: AlertModalType['onClose'],
  ) => showModal({
    title,
    label,
    transitionErrors,
    type: AlertModalTypeEnum.DEFAULT,
    IconComponent: SuccessIcon,
    onClose,
  });

  const showWarningModal = (
    title: AlertModalType['title'],
    label: string,
    transitionErrors: TransitionError[],
    onClose?: AlertModalType['onClose'],
  ) => showModal({
    title,
    label,
    transitionErrors,
    type: AlertModalTypeEnum.DEFAULT,
    IconComponent: WarningIcon,
    onClose,
  });

  const showFailureModal = (
    title: AlertModalType['title'],
    label: string,
    transitionErrors: TransitionError[],
    onConfirm?: AlertModalType['onConfirm'],
  ) => showModal({
    title,
    label,
    transitionErrors,
    isLabelBold: true,
    type: AlertModalTypeEnum.WARNING_IRREVERSIBLE,
    IconComponent: WarningIcon,
    onConfirm,
  });

  const showTransitionToast = (
    type: ToastStatus,
    label: string,
    transitionErrors: TransitionError[],
  ) => addToastNotification({
    type,
    text: (
      <TransitionResultMessages
        label={label}
        transitionErrors={transitionErrors}
      />
    ),
  });

  return {
    showModal,
    showSuccessModal,
    showWarningModal,
    showFailureModal,
    showTransitionToast,
  };
}
